import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import grey from '@material-ui/core/colors/grey';
import { makeStyles, Button, Box, Grid, Theme } from '@material-ui/core';
import { Form as FormEngine, FormDefinition } from '@mylicenseone/forms';
import { DropDownField, Form } from '../../../store/modules/application/types';
import FormFieldDisplay from '../../../components/FormDisplay/FormFieldDisplay';
import { PersonTypeConfiguration } from '../../../store/modules/person/types';
import { restoreRecord } from '../../../components/FormDisplay/FormDisplay';
import { ErrorState } from '../../../components/FormDisplay/types';
import AutoCompleteSelect from '../../../components/AutoCompleteSelect';

type AddPersonDialogProps = {
    addPerson: Function;
    setAddPersonModalOpen: Function;
    getPersonTypeFormById: Function;
    personTypes: PersonTypeConfiguration[];
    personTypeForm: Form;
};

const AddPersonDialog: FunctionComponent<AddPersonDialogProps> = (props) => {
    const useStyles = makeStyles((theme: Theme) => ({
        addPersonDialog: {
            display: 'flex',
            flexGrow: 1,
        },
        modalHeader: {
            color: theme.palette.text.secondary,

            fontSize: 20,
            fontWeight: 500,
            marginBottom: 20,
        },
        margin: {
            margin: theme.spacing(1),
        },
        modalBtn: {
            marginLeft: 0,
        },
        cancelBtn: {
            marginLeft: 0,
            marginRight: '10px',
        },
        btnContainer: {
            marginTop: 25,
        },
        addPersonContainer: {
            padding: 10,
        },
        addPersonDialogLabel: {
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: 'end',
            justifyContent: 'flex-end',
            width: '100%',
            lineHeight: '21px',
            paddingRight: '20px',
            display: 'flex',
        },
        addPersonDialogValue: {
            fontSize: 14,
            fontWeight: 450,
            color: grey[600],
            width: '100%',
            lineHeight: '21px',
        },
        MuiOutlinedInput: {
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: 5,
            '&:focus': {
                backgroundColor: 'white',
                border: '1px solid #ced4da',
            },
            '&:hover': {
                backgroundColor: ['white', '!important'],
                border: '1px solid #ced4da',
            },
        },
        addPersonDialogMain: {
            width: '100%',
        },
        borderedInputs: {
            border: '1px solid #ced4da',
            padding: 5,
            borderRadius: 4,
            marginBottom: '5px',
            '&:focus': {
                borderRadius: 4,
                borderColor: '#80bdff',
                boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
            },
        },
        width: {
            width: '100%',
        },
    }));
    const classes = useStyles();
    const history = useHistory();

    const { setAddPersonModalOpen, addPerson, personTypes, getPersonTypeFormById, personTypeForm } = props;
    const [selectedPersonType, setSelectedPersonType] = useState<DropDownField | null>(null);
    const [selectedPersonTypeName, setSelectedPersonTypeName] = useState<string | undefined>('');
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [formEngine, setFormEngine] = useState<FormEngine>();
    const [formErrors, setFormErrors] = useState<Record<string, ErrorState>>({});
    const [personTypeFormId, setPersonTypeFormId] = useState<string>();

    const formErrorsRef = useRef<Record<string, ErrorState>>({});

    useEffect(() => {
        formEngine?.evaluateApplicationData(formData, { validateDefinedOnly: true });
    }, [formEngine, formData]);

    useEffect(() => {
        if (personTypeForm.sections) {
            const engine = new FormEngine(personTypeForm as FormDefinition);

            engine.onValidationChange((field, error, messages) => {
                formErrorsRef.current = {
                    ...formErrorsRef.current,
                    [field]: {
                        error,
                        messages,
                    },
                };

                setFormErrors(formErrorsRef.current);
            });

            setFormEngine(engine);
        } else {
            setFormEngine(undefined);
        }

        setFormData({});
        setFormErrors({});
    }, [personTypeForm]);

    const handleChange = (data: DropDownField | null) => {
        setFormData({});
        setFormErrors({});
        setSelectedPersonType(data);
        setSelectedPersonTypeName(personTypes.find((personType) => personType.id === data?.value)?.name);

        const formId = personTypes.find((personType) => personType.id === data?.value)?.views?.add?.formId;

        setPersonTypeFormId(formId);
        getPersonTypeFormById(formId);
    };

    const handleInputChange = (dataKey: any, value: string) => {
        setFormData({
            ...formData,
            [dataKey]: value,
        });
    };
    const handleAddPerson = async () => {
        await formEngine?.evaluateApplicationData(formData);

        if (!formEngine?.applicationHasErrors()) {
            const personRecord = restoreRecord({}, formData, personTypeForm);
            const addPersonInfo = {
                ...personRecord,
                type: {
                    id: selectedPersonType?.value,
                    name: selectedPersonTypeName,
                },
            };
            addPerson(addPersonInfo, history);
        }
    };

    const formattedPersonTypes = personTypes.map((type) => {
        return { label: type.name, value: type.id };
    });

    const checkDisable = () => {
        return (
            !selectedPersonTypeName ||
            (!!personTypeFormId && !personTypeForm.sections) ||
            Object.values(formErrors).some((error) => error.error)
        );
    };

    return (
        <Box className={classes.addPersonDialog}>
            <Grid container className={classes.addPersonContainer}>
                <div className={classes.modalHeader}>Add Licensee</div>
                <Box component="div" className={classes.addPersonDialogMain}>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={3} sm={3} md={3} lg={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addPersonDialogLabel}>
                                Licensee Type
                            </Box>
                        </Grid>
                        <Grid item xs={9} sm={9} md={9} lg={9}>
                            <Box className={classes.addPersonDialogValue}>
                                <AutoCompleteSelect
                                    data={formattedPersonTypes}
                                    onChange={handleChange}
                                    inputSize={'small'}
                                    value={selectedPersonType}
                                    disableClearable={true}
                                    variant={'outlined'}
                                    ariaLabel="Licensee Type"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    {!!personTypeForm?.id &&
                        selectedPersonType &&
                        personTypeForm?.sections.map((section) => {
                            return section.entries.map((entry) => {
                                return (
                                    <Grid key={entry.field} container style={{ marginTop: '10px', width: '100%' }}>
                                        <Grid
                                            key={entry.field}
                                            item
                                            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                        >
                                            <FormFieldDisplay
                                                formData={formData}
                                                formField={entry}
                                                value={formData[entry.field]}
                                                error={formErrors[entry.field]}
                                                readOnly={false}
                                                handleInputChange={handleInputChange}
                                                isCustomStyle={true}
                                            />
                                        </Grid>
                                    </Grid>
                                );
                            });
                        })}
                </Box>
                <Grid className={classes.btnContainer} sm={12} md={12} lg={12} item>
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setAddPersonModalOpen(false);
                            }}
                            className={classes.cancelBtn}
                        >
                            CANCEL
                        </Button>
                        <Button
                            className={classes.modalBtn}
                            onClick={handleAddPerson}
                            variant="contained"
                            color="primary"
                            disabled={checkDisable()}
                        >
                            ADD
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddPersonDialog;
